
import { computed, defineComponent, inject, onMounted, reactive, ref } from 'vue';
import CustomerCoupon from '@/components/organisms/coupon/CustomerCoupon.vue';
import SkeltonCoupon from '@/components/organisms/coupon/SkeltonCoupon.vue';
import { useCoupon } from '@/compositions/useCoupon';
import { usePoint } from '@/compositions/usePoint';
import { CustomerCoupon as TypeCustomerCoupon } from '@/repositories/couponRepository';
import SVGProduct from '@/assets/product.svg?data';
import { MultipleCouponKey } from '@/compositions/useMultipleCoupon';
import { useRouter, useRoute } from 'vue-router/composables';
import { useShop, SHOP_DETAILS_KEY } from '@/compositions/useShop';
import { useData } from '@/compositions/useData';
import { Shop } from '@/repositories/shopRepository';

type State = {
  initialized: boolean;
};
export default defineComponent({
  name: 'PagesCouponList',

  components: {
    CustomerCoupon,
    SkeltonCoupon,
  },

  setup() {
    const multipleCouponInjection = inject(MultipleCouponKey);
    if (!multipleCouponInjection) throw new Error(`NO PROVIDE ${MultipleCouponKey}`);
    const {
      customerCoupons: selectedCustomerCoupons,
      add,
      remove,
      clear,
      has,
      length,
      addable,
    } = multipleCouponInjection;

    const { getCustomerCoupon, available, valid, isOtherChannelCoupon } = useCoupon();
    const { enabled: pointEnabled } = usePoint();
    const router = useRouter();
    const route = useRoute();
    const { getDetails } = useShop();
    const { set: setData } = useData();

    const state = reactive<State>({
      initialized: false,
    });

    const activeTab = ref(0);
    const currentCustomerCoupon = ref<TypeCustomerCoupon | null>(null);
    const customerCoupons = ref<TypeCustomerCoupon[]>([]);
    const targetCouponId = ref<string>('');

    const customerCouponRefs = ref<InstanceType<typeof CustomerCoupon>[]>();

    /**
     * クーポンから店舗IDを抽出する
     */
    const extractShopIds = (coupons: TypeCustomerCoupon[]): string[] => {
      const shopIds: string[] = [];
      coupons.forEach(coupon => {
        if (!coupon.contents) return;
        try {
          const contents = JSON.parse(coupon.contents);
          if (contents.shopId) {
            shopIds.push(contents.shopId);
          }
        } catch (e) {
          console.warn('Failed to parse coupon contents', e);
        }
      });
      // 重複を削除
      return [...new Set(shopIds)];
    };

    /**
     * 店舗情報を一括取得して保存する
     */
    const fetchAndStoreShopDetails = async (coupons: TypeCustomerCoupon[]) => {
      const shopIds = extractShopIds(coupons);
      if (!shopIds.length) return;

      try {
        const shops = await getDetails(shopIds);
        if (!shops || !shops.length) return;

        const shopMap = new Map<string, Shop>();
        shops.forEach((shop: Shop) => {
          shopMap.set(shop.id, shop);
        });
        setData(SHOP_DETAILS_KEY, shopMap);
      } catch (e) {
        console.error('Failed to fetch shop details', e);
      }
    };

    const showUsageScreen = (coupon: TypeCustomerCoupon, idx: number) => {
      currentCustomerCoupon.value = coupon;
      customerCouponRefs.value?.[idx].close(); // クーポン利用確認画面を表示する際は、クーポン詳細ダイアログを閉じる
      router.push(`/coupon/${coupon.id}`);
    };

    /**
     * 有効クーポン
     * @note 利用可能期間開始前のクーポンも含む
     */
    const validCustomerCoupons = computed(() => customerCoupons.value.filter(item => valid(item)));

    /**
     * 無効クーポン
     * @note 使用済み および 期限切れクーポンが該当する
     */
    const invalidCustomerCoupons = computed(() =>
      customerCoupons.value.filter(item => !valid(item) && !isOtherChannelCoupon(item.channelId))
    );

    /**
     * まとめて使う機能を表示するかどうか
     * @note Channelモデルで設定された値を参照
     */

    const hideMultipleConsumeCouponFunction = computed(() => {
      return (
        `${
          process.env.NODE_ENV === 'local'
            ? process.env.VUE_APP_HIDE_MULTIPLE_CONSUME_COUPON_FUNCTION
            : window.HIDE_MULTIPLE_CONSUME_COUPON_FUNCTION
        }` === 'true'
      );
    });

    const _FOOTER_DISABLED: boolean =
      `${
        process.env.NODE_ENV === 'local'
          ? window.FOOTER_DISABLED || process.env.VUE_APP_FOOTER_DISABLED
          : window.FOOTER_DISABLED
      }` === 'true';
    // const FOOTER_DISABLED: boolean = _FOOTER_DISABLED || !pointEnabled.value;
    const FOOTER_DISABLED: boolean = _FOOTER_DISABLED; // CS-6437

    /**
     * CS-9423 該当クーポンがどのタブに存在するかを判定し、最初にマッチしたクーポンを返す
     */
    const findCouponTab = (couponMasterId: string): { tab: number; couponId: string | null } => {
      // 利用可能タブから検索
      const validCoupon = validCustomerCoupons.value.find(c => c.couponMasterId === couponMasterId);
      if (validCoupon) return { tab: 0, couponId: validCoupon.id };

      // 利用履歴タブから検索
      const invalidCoupon = invalidCustomerCoupons.value.find(c => c.couponMasterId === couponMasterId);
      if (invalidCoupon) return { tab: 1, couponId: invalidCoupon.id };

      return { tab: 0, couponId: null }; // 見つからない場合
    };

    /**
     * クーポンが使用されたら実行される関数
     */
    const onUsed = async () => {
      // 顧客クーポン再読み込み
      state.initialized = false;
      const { customerCoupons: _customerCoupons } = await getCustomerCoupon();
      customerCoupons.value = _customerCoupons;
      await fetchAndStoreShopDetails(_customerCoupons);
      state.initialized = true;

      // クーポン選択クリア
      clear();
    };

    // CS-9351: 選択したクーポンの対象店舗が競合していないか
    const isShopTargetConflict = () => {
      // 全店舗選択のものを省く
      const haveShopIdCoupons = selectedCustomerCoupons.value.filter(
        coupon => coupon.coupon.shopIds && coupon.coupon.shopIds.length > 0
      );

      // クーポンがまとめて選択されていない、もしくは全店舗選択のクーポンしかない場合はtrue
      if (haveShopIdCoupons.length === 0) {
        return true;
      }

      let commonShopIds = new Set<string>(haveShopIdCoupons[0].coupon.shopIds);

      for (let i = 1; i < haveShopIdCoupons.length; i++) {
        const currentCouponShopIds = new Set(haveShopIdCoupons[i].coupon.shopIds);
        const newCommonShopIds = new Set<string>();

        for (const id of commonShopIds) {
          // 重複してるIdがあるか確認
          if (currentCouponShopIds.has(id)) {
            newCommonShopIds.add(id);
          }
        }
        commonShopIds = newCommonShopIds;

        // 共通の店舗IDがない場合
        if (commonShopIds.size === 0) {
          return false;
        }
      }

      // 共通の店舗IDがある場合
      return commonShopIds.size > 0;
    };

    onMounted(async () => {
      const { customerCoupons: _customerCoupons } = await getCustomerCoupon();
      customerCoupons.value = _customerCoupons;
      await fetchAndStoreShopDetails(_customerCoupons);
      state.initialized = true;

      // CS-9432 クエリパラメータをチェックしてタブ切り替えとモーダル表示
      const openDetailId = route.query['open-detail'] as string;
      const autoSwitchTab = route.query['auto-switch-tab'];

      if (openDetailId && autoSwitchTab) {
        const result = findCouponTab(openDetailId);
        activeTab.value = result.tab;
        targetCouponId.value = result.couponId || '';
      }
      // モーダル表示後にクエリパラメータをクリア
      router.replace({ query: {} }).catch(() => {
        // ルート変更エラーを無視
      });
    });

    return {
      state,
      activeTab,
      addable,
      customerCouponRefs,
      currentCustomerCoupon,
      customerCoupons,
      FOOTER_DISABLED,
      invalidCustomerCoupons,
      hideMultipleConsumeCouponFunction,
      length,
      pointEnabled,
      selectedCustomerCoupons,
      svg: {
        product: SVGProduct,
      },
      targetCouponId,
      validCustomerCoupons,

      //
      add,
      available,
      has,
      onUsed,
      remove,
      showUsageScreen,
      valid,
      isShopTargetConflict,
    };
  },
});
